import type { SubmissionFilters, SubmissionSort } from './queries'
import type { SubmissionTabCounts } from '@/types/api'
import { addDays } from './timezone'

/**
 * The reports list as a set of queues, and everything about it that lives in
 * the address bar.
 *
 * ## Why the URL holds all of it
 *
 * The list is the screen people link to — the bell's reminders, Početna's
 * numbers, a supervisor pasting "look at these" into a chat — and a link that
 * opens on a different set of rows than the sender was looking at is worse
 * than no link. So the tab, every filter, the order and the page are read from
 * the query string and written back to it; nothing about the list is kept in
 * component state that a reload would lose.
 *
 * ## Why two vocabularies
 *
 * The address bar speaks the user's language (`?tab=van-roka`), because people
 * read and type these; the API speaks its own (`tab=overdue`), because every
 * client and the exports share it. The slugs are a contract with Početna,
 * which links straight into them — rename one and its numbers open the wrong
 * list without anything failing.
 */

export type QueueTab =
  | 'otvorene'
  | 'nove'
  | 'u-radu'
  | 'van-roka'
  | 'rijesene'
  | 'dodijeljene-meni'
  | 'moje'
  | 'sve'

export type ApiTab = keyof SubmissionTabCounts

export const DEFAULT_TAB: QueueTab = 'otvorene'

/**
 * Every tab, in the order they sit above the list.
 *
 * `managersOnly` is about meaning, not permission — the server scopes rows by
 * itself. A worker sees only their own reports, so "Moje prijave" already is
 * everything they have; "Sve" would be the same list twice, and nobody hands a
 * worker a report, so "Dodijeljene meni" would always be empty.
 */
export const QUEUE_TABS: readonly { slug: QueueTab; api: ApiTab; managersOnly?: boolean }[] = [
  { slug: 'otvorene', api: 'open' },
  { slug: 'nove', api: 'new' },
  { slug: 'u-radu', api: 'in_progress' },
  { slug: 'van-roka', api: 'overdue' },
  { slug: 'rijesene', api: 'resolved' },
  { slug: 'dodijeljene-meni', api: 'assigned_to_me', managersOnly: true },
  { slug: 'moje', api: 'mine' },
  { slug: 'sve', api: 'all', managersOnly: true },
]

export function tabsFor(isManager: boolean) {
  return QUEUE_TABS.filter((tab) => isManager || !tab.managersOnly)
}

export function apiTab(slug: QueueTab): ApiTab {
  return QUEUE_TABS.find((tab) => tab.slug === slug)!.api
}

export type Direction = 'asc' | 'desc'

/** What the list shows, all of it from the address bar. Empty string means "any". */
export interface QueueState {
  tab: QueueTab
  search: string
  type: string
  priority: string
  status: string
  workCenterId: string
  plantId: string
  from: string
  to: string
  /** Nobody is on it. A chip, because it narrows any tab. */
  unassigned: boolean
  /**
   * Late, on a tab other than "Van roka" — what the bell's "late and yours"
   * reminder opens on. On "Van roka" itself it would say nothing.
   */
  overdue: boolean
  sort: SubmissionSort
  /** Null means the column's natural direction. */
  direction: Direction | null
  page: number
}

const SORTS: readonly SubmissionSort[] = [
  'newest',
  'oldest',
  'priority',
  'due',
  'status',
  'type',
  'title',
  'workCenter',
  'assignee',
]

/**
 * The order a tab opens in. "Van roka" is a list of deadlines already missed,
 * and the one missed longest ago is the one to look at first.
 */
export function defaultSort(tab: QueueTab): SubmissionSort {
  return tab === 'van-roka' ? 'due' : 'newest'
}

function isTab(value: string | null, isManager: boolean): value is QueueTab {
  return tabsFor(isManager).some((tab) => tab.slug === value)
}

const truthy = (value: string | null) => value === 'true' || value === '1'

/**
 * The list's state, out of a query string.
 *
 * Forgiving on purpose: an unknown tab, a tab this role does not have, a sort
 * nobody offers or a page of "abc" all fall back to the default rather than to
 * an error, because the string arrives from links people bookmark and paste.
 *
 * ## The links from before the tabs
 *
 * The reminder notifications already sitting in people's bells, and Početna
 * until it moves over, link with `?overdue=true`, `?mine=true` (which meant
 * "assigned to me") and `?otvorene=true`. They keep working: `overdue` alone is
 * the "Van roka" tab, `mine` is "Dodijeljene meni" — with `overdue` as a chip
 * when both came, which is exactly what "late and yours" asked for — and
 * `otvorene` is the default anyway.
 */
export function parseQueue(params: URLSearchParams, isManager: boolean): QueueState {
  const requested = params.get('tab')
  let overdue = truthy(params.get('overdue'))
  let tab: QueueTab

  if (isTab(requested, isManager)) {
    tab = requested
  } else if (requested === null && truthy(params.get('mine')) && isManager) {
    tab = 'dodijeljene-meni'
  } else if (requested === null && overdue) {
    tab = 'van-roka'
  } else {
    tab = DEFAULT_TAB
  }

  if (tab === 'van-roka') overdue = false

  const sortParam = params.get('sort') as SubmissionSort | null
  const directionParam = params.get('dir')
  const page = Number.parseInt(params.get('page') ?? '', 10)

  return {
    tab,
    search: params.get('search') ?? '',
    type: params.get('type') ?? '',
    priority: params.get('priority') ?? '',
    status: params.get('status') ?? '',
    workCenterId: params.get('workCenterId') ?? '',
    plantId: params.get('plantId') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    unassigned: isManager && truthy(params.get('unassigned')),
    overdue,
    sort: sortParam && SORTS.includes(sortParam) ? sortParam : defaultSort(tab),
    direction: directionParam === 'asc' || directionParam === 'desc' ? directionParam : null,
    page: Number.isFinite(page) && page > 1 ? page : 1,
  }
}

/**
 * The state as a query string, leaving out every default.
 *
 * The default tab is left out too, so the plain `/prijave` link and the
 * sidebar entry stay one address rather than two.
 */
export function serializeQueue(state: QueueState): URLSearchParams {
  const params = new URLSearchParams()

  if (state.tab !== DEFAULT_TAB) params.set('tab', state.tab)
  if (state.search) params.set('search', state.search)
  if (state.type) params.set('type', state.type)
  if (state.priority) params.set('priority', state.priority)
  if (state.status) params.set('status', state.status)
  if (state.workCenterId) params.set('workCenterId', state.workCenterId)
  if (state.plantId) params.set('plantId', state.plantId)
  if (state.from) params.set('from', state.from)
  if (state.to) params.set('to', state.to)
  if (state.unassigned) params.set('unassigned', 'true')
  if (state.overdue && state.tab !== 'van-roka') params.set('overdue', 'true')
  if (state.sort !== defaultSort(state.tab)) params.set('sort', state.sort)
  if (state.direction) params.set('dir', state.direction)
  if (state.page > 1) params.set('page', String(state.page))

  return params
}

/**
 * Change part of the state, going back to page one unless the change *was*
 * the page — page three of a different filter is a page nobody asked for.
 *
 * A new tab also drops a column order that was only the old tab's default,
 * so "Van roka" opens by deadline and leaving it does not keep that.
 */
export function updateQueue(state: QueueState, patch: Partial<QueueState>): QueueState {
  const next = { ...state, ...patch }

  if (!('page' in patch)) next.page = 1

  if (patch.tab && patch.tab !== state.tab && !('sort' in patch)) {
    if (state.sort === defaultSort(state.tab) && state.direction === null) {
      next.sort = defaultSort(patch.tab)
    }
    if (patch.tab === 'van-roka') next.overdue = false
  }

  return next
}

/** Every filter the API takes, apart from the tab, the order and the page. */
export function countFilters(state: QueueState) {
  return {
    search: state.search || undefined,
    type: state.type ? [state.type] : undefined,
    priority: state.priority ? [state.priority] : undefined,
    status: state.status ? [state.status] : undefined,
    workCenterId: state.workCenterId || undefined,
    plantId: state.plantId || undefined,
    from: state.from || undefined,
    to: state.to || undefined,
    unassigned: state.unassigned || undefined,
    overdue: (state.overdue && state.tab !== 'van-roka') || undefined,
  } satisfies SubmissionFilters
}

/**
 * The list's request, minus the page. Also what the exports are sent, so a
 * spreadsheet downloaded from a tab is that tab and nothing else.
 */
export function listFilters(state: QueueState): SubmissionFilters {
  return {
    ...countFilters(state),
    tab: apiTab(state.tab),
    sort: state.sort,
    direction: state.direction ?? undefined,
  }
}

/** How many of the removable filters are on — the tab is not one of them. */
export function activeFilterCount(state: QueueState): number {
  return [
    state.type,
    state.priority,
    state.status,
    state.workCenterId,
    state.plantId,
    state.from || state.to,
    state.unassigned,
    state.overdue,
  ].filter(Boolean).length
}

/** Everything but the tab and the order back to "any". */
export function clearFilters(state: QueueState): QueueState {
  return {
    ...state,
    search: '',
    type: '',
    priority: '',
    status: '',
    workCenterId: '',
    plantId: '',
    from: '',
    to: '',
    unassigned: false,
    overdue: false,
    page: 1,
  }
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export type SortColumn =
  | 'created'
  | 'priority'
  | 'due'
  | 'status'
  | 'type'
  | 'title'
  | 'workCenter'
  | 'assignee'

/**
 * Which way a column sorts the first time it is clicked — the way somebody
 * would read it. The most urgent priority first; the nearest deadline first;
 * the workflow's own order for statuses; the alphabet for names.
 */
export const NATURAL_DIRECTION: Record<SortColumn, Direction> = {
  created: 'desc',
  priority: 'desc',
  due: 'asc',
  status: 'asc',
  type: 'asc',
  title: 'asc',
  workCenter: 'asc',
  assignee: 'asc',
}

/** The column the list is sorted by now, and which way. */
export function activeSort(state: Pick<QueueState, 'sort' | 'direction'>): {
  column: SortColumn
  direction: Direction
} {
  if (state.sort === 'newest') return { column: 'created', direction: 'desc' }
  if (state.sort === 'oldest') return { column: 'created', direction: 'asc' }

  const column = state.sort as SortColumn

  return { column, direction: state.direction ?? NATURAL_DIRECTION[column] }
}

/**
 * A click on a column heading: the column in its natural direction, or the
 * other way round if it was already sorted by it.
 *
 * "Prijavljeno" is spelled through the API's two old names rather than a
 * direction, which is why it is a special case here and nowhere else.
 */
export function toggleSort(
  state: Pick<QueueState, 'sort' | 'direction'>,
  column: SortColumn,
): Pick<QueueState, 'sort' | 'direction'> {
  const current = activeSort(state)
  const flipping = current.column === column
  const direction: Direction = flipping
    ? current.direction === 'asc'
      ? 'desc'
      : 'asc'
    : NATURAL_DIRECTION[column]

  if (column === 'created') {
    return { sort: direction === 'desc' ? 'newest' : 'oldest', direction: null }
  }

  return {
    sort: column,
    direction: direction === NATURAL_DIRECTION[column] ? null : direction,
  }
}

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------

export type PeriodPreset = '7d' | '30d' | '90d' | 'month' | 'lastMonth'

export const PERIOD_PRESETS: readonly PeriodPreset[] = ['7d', '30d', '90d', 'month', 'lastMonth']

/**
 * A preset as two calendar dates, counted from `today` on the company's clock
 * — `lib/timezone.ts` says why the device's own date is the wrong one.
 * Inclusive at both ends, which is how the API reads `from` and `to`.
 */
export function periodRange(preset: PeriodPreset, today: string): { from: string; to: string } {
  const [year, month] = today.split('-').map(Number)
  const firstOfMonth = `${year}-${String(month).padStart(2, '0')}-01`

  switch (preset) {
    case '7d':
      return { from: addDays(today, -6), to: today }
    case '30d':
      return { from: addDays(today, -29), to: today }
    case '90d':
      return { from: addDays(today, -89), to: today }
    case 'month':
      return { from: firstOfMonth, to: today }
    case 'lastMonth': {
      const lastOfPrevious = addDays(firstOfMonth, -1)

      return { from: `${lastOfPrevious.slice(0, 7)}-01`, to: lastOfPrevious }
    }
  }
}

/** The preset a range is, if it is one, so the chip can say its name. */
export function matchPeriod(from: string, to: string, today: string): PeriodPreset | null {
  return (
    PERIOD_PRESETS.find((preset) => {
      const range = periodRange(preset, today)

      return range.from === from && range.to === to
    }) ?? null
  )
}
